import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Steps, Spin } from "antd";
import { notifyUser } from "../../util/util";
import {
  serverRequest,
  serverRequestNoBearer,
} from "../../util/server_request";

// Check if mock mode is enabled
const isMockMode = process.env.REACT_APP_ADD_COMPANY_MOCK === "true";
console.log(
  "Add Company Mock Mode:",
  isMockMode,
  "Env Value:",
  process.env.REACT_APP_ADD_COMPANY_MOCK,
);

// Step 1: Employer Number
const StepEmployerNumber = ({ formData, setFormData, onNext }) => {
  const [loading, setLoading] = useState(false);

  const handleSubmit = () => {
    // Mock mode: skip validation and API call
    if (isMockMode) {
      notifyUser("success", "Employer verified (Mock Mode)", "Success");
      const mockPhone = "0501519380";
      setFormData({
        ...formData,
        phone: mockPhone,
        employerNumber: formData.employerNumber || "MOCK001",
      });
      onNext();
      return;
    }

    if (!formData.employerNumber) {
      return notifyUser(
        "error",
        "Please enter employer number",
        "Validation Error",
      );
    }

    setLoading(true);
    serverRequest("POST", "profile/employers", {
      employerNumber: formData.employerNumber,
    })
      .then((result) => {
        setLoading(false);
        if (result?.code === "200") {
          notifyUser("success", result.message, "Success");
          setFormData({ ...formData, phone: result?.data?.phone });
          onNext();
        } else {
          notifyUser(
            "error",
            result?.message || "Verification failed",
            "Error",
          );
        }
      })
      .catch((error) => {
        setLoading(false);
        notifyUser("error", error, "Error");
      });
  };

  return (
    <div className="mx-auto mobile-full-width shadow bg-white p-5 rounded login-width">
      <div className="text-start">
        <p className="lead fw-bold">Enter Employer Number</p>
        <small className="text-muted">
          Input The Employer number to the company to link
        </small>

        {isMockMode && (
          <div className="alert alert-warning mt-3" style={{ fontSize: 12 }}>
            <i className="bi bi-info-circle" /> Mock Mode: Click Continue to
            proceed
          </div>
        )}

        <Spin spinning={loading}>
          <div className="mt-4">
            <div className="mb-5 d-flex justify-content-center">
              <div style={{ maxWidth: 400, width: "100%" }}>
                <omk-text-field
                  label="Employer Number"
                  value={formData.employerNumber}
                  type="text"
                  onInput={(e) =>
                    setFormData({ ...formData, employerNumber: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="text-center">
              <button
                className="btn btn-primary mb-4 w-50 mobile-full-width"
                onClick={handleSubmit}
              >
                Continue
              </button>
            </div>
          </div>
        </Spin>
      </div>
    </div>
  );
};

// Step 2: OTP Verification
const StepOTPVerification = ({ formData, setFormData, onNext, onBack }) => {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  // Countdown timer
  React.useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => setCountdown((c) => c - 1), 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  const handleVerify = () => {
    // Mock mode: skip validation and API call
    if (isMockMode) {
      notifyUser("success", "OTP verified (Mock Mode)", "Success");
      onNext();
      return;
    }

    if (!otp) {
      return notifyUser("error", "Please enter OTP", "Validation Error");
    }

    setLoading(true);
    serverRequestNoBearer(
      "GET",
      `auth/verify/phone/otp?otp=${otp}&phone=${formData.phone}`,
    )
      .then((result) => {
        setLoading(false);
        if (result?.success) {
          notifyUser("success", "OTP verified successfully", "Success");
          onNext();
        } else {
          notifyUser("error", result?.message || "Invalid OTP", "Error");
        }
      })
      .catch((error) => {
        setLoading(false);
        notifyUser("error", error, "Error");
      });
  };

  const handleResend = () => {
    if (!canResend) return;

    // Mock mode: skip API call
    if (isMockMode) {
      setCountdown(60);
      setCanResend(false);
      notifyUser("success", "OTP resent (Mock Mode)", "Success");
      return;
    }

    setLoading(true);
    serverRequestNoBearer("POST", "auth/phone-verification/otp/send", {
      phone: formData.phone,
      employerNumber: formData.employerNumber,
    })
      .then(() => {
        setLoading(false);
        setCountdown(60);
        setCanResend(false);
        notifyUser("success", "OTP resent successfully", "Success");
      })
      .catch((error) => {
        setLoading(false);
        notifyUser("error", error, "Error");
      });
  };

  const formatCountdown = () => {
    const mins = Math.floor(countdown / 60);
    const secs = countdown % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="mx-auto mobile-full-width shadow bg-white p-5 rounded login-width">
      <div className="text-start">
        <p className="lead fw-bold">Verify OTP</p>
        <small className="text-muted">
          Enter OTP sent to{" "}
          <strong>
            {formData.phone
              ? formData.phone.slice(0, 4) + "****" + formData.phone.slice(-3)
              : "****"}
          </strong>
        </small>

        {isMockMode && (
          <div className="alert alert-warning mt-3" style={{ fontSize: 12 }}>
            <i className="bi bi-info-circle" /> Mock Mode: Click Continue to
            proceed
          </div>
        )}

        <Spin spinning={loading}>
          <div className="mt-4">
            <omk-otp-field
              number-of-inputs="6"
              resend-text=""
              resend-button-label=""
              label="One-time Password"
              className="mb-2"
              value={otp}
              onInput={(e) => setOtp(e.target.value)}
            />

            <div className="mb-3 text-start">
              {!canResend ? (
                <p className="mb-0 text-muted">
                  Resend OTP in{" "}
                  <span style={{ fontWeight: "bold", color: "#009677" }}>
                    {formatCountdown()}
                  </span>
                </p>
              ) : (
                <p
                  className="mb-0"
                  style={{ cursor: "pointer" }}
                  onClick={handleResend}
                >
                  Didn't receive OTP?{" "}
                  <span style={{ color: "#009677", fontWeight: "bold" }}>
                    Resend
                  </span>
                </p>
              )}
            </div>

            <div className="d-flex gap-2 justify-content-center">
              <button className="btn btn-white mb-4" onClick={onBack}>
                Back
              </button>
              <button className="btn btn-primary mb-4" onClick={handleVerify}>
                Continue
              </button>
            </div>
          </div>
        </Spin>
      </div>
    </div>
  );
};

// Step 3: Tax Verification
const StepProfileVerification = ({
  formData,
  setFormData,
  onNext,
  onBack,
  onLinkSuccess,
}) => {
  const [loading, setLoading] = useState(false);

  const handleSubmit = () => {
    // Mock mode: skip validation and API call
    if (isMockMode) {
      notifyUser(
        "success",
        "Company verified and linked (Mock Mode)",
        "Success",
      );
      setFormData({
        ...formData,
        taxNumber: formData.taxNumber || "TAX001",
        registrationNumber: formData.registrationNumber || "REG001",
      });
      onNext();
      return;
    }

    if (!formData.taxNumber || !formData.registrationNumber) {
      return notifyUser("error", "Please fill all fields", "Validation Error");
    }

    setLoading(true);
    // This endpoint requires authentication
    serverRequest("POST", "profile/verify/employer/details", {
      employerNumber: formData.employerNumber,
      taxNumber: formData.taxNumber,
      registrationNumber: formData.registrationNumber,
    })
      .then((result) => {
        setLoading(false);
        if (result?.success) {
          notifyUser("success", result.message, "Success");
          // Pass the linked company data to parent for Step 4
          // API returns: { employerNumber, companyName }
          onLinkSuccess(
            result?.data || {
              employerNumber: formData.employerNumber,
              companyName: formData.employerNumber,
            },
          );
          onNext();
        } else {
          notifyUser(
            "error",
            result?.message || "Verification failed",
            "Error",
          );
        }
      })
      .catch((error) => {
        setLoading(false);
        notifyUser("error", error, "Error");
      });
  };

  return (
    <div className="mx-auto mobile-full-width shadow bg-white p-5 rounded login-width">
      <div className="text-start">
        <p className="lead fw-bold">Profile Verification</p>
        <small className="text-muted">Input Tax Number to Verify company</small>

        {isMockMode && (
          <div className="alert alert-warning mt-3" style={{ fontSize: 12 }}>
            <i className="bi bi-info-circle" /> Mock Mode: Click Continue to
            proceed
          </div>
        )}

        <Spin spinning={loading}>
          <div className="mt-3">
            {/* Fields stacked vertically */}
            <div className="mb-3 d-flex justify-content-center">
              <div style={{ maxWidth: 400, width: "100%" }}>
                <omk-text-field
                  label="Company Registration Number"
                  value={formData.registrationNumber}
                  type="text"
                  onInput={(e) =>
                    setFormData({
                      ...formData,
                      registrationNumber: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="mb-4 d-flex justify-content-center">
              <div style={{ maxWidth: 400, width: "100%" }}>
                <omk-text-field
                  label="Tax Number"
                  value={formData.taxNumber}
                  type="text"
                  onInput={(e) =>
                    setFormData({ ...formData, taxNumber: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="d-flex gap-2 justify-content-center">
              <button className="btn btn-white mb-4" onClick={onBack}>
                Back
              </button>
              <button className="btn btn-primary mb-4" onClick={handleSubmit}>
                Verify & Link Company
              </button>
            </div>
          </div>
        </Spin>
      </div>
    </div>
  );
};

// Step 4: Company Link (Success)
const StepCompanyLink = ({ linkedCompany }) => {
  const [switching, setSwitching] = useState(false);

  const handleGoToDashboard = () => {
    // Full refresh to load new company data
    window.location.href = "/dashboard";
  };

  const handleSwitchCompany = () => {
    setSwitching(true);
    // Call switch API endpoint
    serverRequest("POST", "profile/switch", {
      employerNumber: linkedCompany?.employerNumber,
    })
      .then((result) => {
        setSwitching(false);
        if (result?.success) {
          notifyUser("success", "Switched to company successfully!", "Success");
          // Full refresh to dashboard to load new company data
          window.location.href = "/dashboard";
        } else {
          notifyUser("error", result?.message || "Switch failed", "Error");
        }
      })
      .catch((error) => {
        setSwitching(false);
        notifyUser("error", error, "Error");
      });
  };

  // API response: { employerNumber, companyName }
  const companyName =
    linkedCompany?.companyName || linkedCompany?.employerNumber || "Company";
  const companyNumber = linkedCompany?.employerNumber || "";

  return (
    <div className="mx-auto mobile-full-width shadow bg-white p-5 rounded login-width">
      <div className="text-center">
        {/* Success Icon */}
        <div className="mb-4">
          <i
            className="bi bi-check-circle-fill"
            style={{ fontSize: "5rem", color: "#009677" }}
          />
        </div>

        {/* Title */}
        <h4 className="fw-bold mb-3" style={{ color: "#000" }}>
          Company Linked
        </h4>

        {/* Description */}
        <p className="text-muted mb-2">
          <strong>{companyName}</strong> {companyNumber && `(${companyNumber})`}{" "}
          has been linked to your account.
        </p>
        <p className="text-muted mb-5">
          You can now switch to this company in the header.
        </p>

        {/* Buttons */}
        <div className="d-flex gap-3 justify-content-center">
          <button
            className="btn btn-primary mb-4 px-4"
            onClick={handleSwitchCompany}
            disabled={switching}
          >
            {switching ? (
              <>
                <Spin size="small" style={{ marginRight: 8 }} />
                Switching...
              </>
            ) : (
              "Switch Company"
            )}
          </button>
          <button
            className="btn btn-white mb-4 px-4"
            onClick={handleGoToDashboard}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Wizard Component
export const AddCompanyPage = () => {
  const location = useLocation();

  // Get employer number from URL query params
  const queryParams = new URLSearchParams(location.search);
  const employerNumberFromUrl = queryParams.get("employerNumber");

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    employerNumber: employerNumberFromUrl || "",
    phone: "",
    taxNumber: "",
    registrationNumber: "",
    email: "",
  });

  // Store linked company info from API response
  const [linkedCompany, setLinkedCompany] = useState(null);

  // Update formData if URL param changes
  useEffect(() => {
    if (employerNumberFromUrl) {
      setFormData((prev) => ({
        ...prev,
        employerNumber: employerNumberFromUrl,
      }));
    }
  }, [employerNumberFromUrl]);

  // Handle successful company link
  const handleLinkSuccess = (companyData) => {
    setLinkedCompany(companyData);
  };

  const steps = [
    { title: "Employer Number" },
    { title: "OTP Verification" },
    { title: "Profile Verification" },
    { title: "Company Link" },
  ];

  const nextStep = () => setCurrentStep((s) => Math.min(s + 1, 3));
  const prevStep = () => setCurrentStep((s) => Math.max(s - 1, 0));

  return (
    <div className="container-fluid py-4">
      {/* Step Progress */}
      <div
        className="mb-4 mx-auto add-company-steps text-center"
        style={{ maxWidth: 900 }}
      >
        <Steps current={currentStep} items={steps} labelPlacement="vertical" />
      </div>

      {/* Custom Step Styles */}
      <style>{`
        .add-company-steps .ant-steps-item-icon {
          background: #fff !important;
          border: 2px solid #d9d9d9 !important;
          border-radius: 50% !important;
          width: 32px !important;
          height: 32px !important;
          line-height: 28px !important;
        }

        .add-company-steps .ant-steps-item-icon .ant-steps-icon {
          color: #999 !important;
          fontWeight: 600 !important;
          font-size: 14px !important;
        }

        /* Active step - green outline with green number */
        .add-company-steps .ant-steps-item-active .ant-steps-item-icon {
          border-color: #009677 !important;
          background: #fff !important;
        }

        .add-company-steps .ant-steps-item-active .ant-steps-icon {
          color: #009677 !important;
        }

        /* Finished step - gradient green bg with white checkmark */
        .add-company-steps .ant-steps-item-finish .ant-steps-item-icon {
          border-color: #009677 !important;
          background: linear-gradient(90deg, #009677, #60b848) !important;
        }

        .add-company-steps .ant-steps-item-finish .ant-steps-icon {
          color: #fff !important;
        }

        .add-company-steps .ant-steps-item-title {
          color: #666 !important;
          font-size: 13px !important;
          line-height: 1.3 !important;
          margin-top: 4px !important;
          white-space: nowrap !important;
        }

        .add-company-steps .ant-steps-item-active .ant-steps-item-title {
          color: #009677 !important;
          font-weight: 500 !important;
          line-height: 1.3 !important;
          white-space: nowrap !important;
        }

        .add-company-steps .ant-steps-item-finish .ant-steps-item-title {
          color: #009677 !important;
          font-weight: 500 !important;
          line-height: 1.3 !important;
          white-space: nowrap !important;
        }

        .add-company-steps .ant-steps-item-tail::after {
          background: #e8e8e8 !important;
        }

        .add-company-steps .ant-steps-item-finish .ant-steps-item-tail::after {
          background: linear-gradient(90deg, #009677, #60b848) !important;
        }
      `}</style>

      {/* Step Content */}
      {currentStep === 0 && (
        <StepEmployerNumber
          formData={formData}
          setFormData={setFormData}
          onNext={nextStep}
        />
      )}
      {currentStep === 1 && (
        <StepOTPVerification
          formData={formData}
          setFormData={setFormData}
          onNext={nextStep}
          onBack={prevStep}
        />
      )}
      {currentStep === 2 && (
        <StepProfileVerification
          formData={formData}
          setFormData={setFormData}
          onNext={nextStep}
          onBack={prevStep}
          onLinkSuccess={handleLinkSuccess}
        />
      )}
      {currentStep === 3 && <StepCompanyLink linkedCompany={linkedCompany} />}
    </div>
  );
};

export default AddCompanyPage;
